// Provider popups for social sign-in. Each one ends with a credential that the
// backend verifies itself (authApi.socialLogin): Google's popup code flow gives
// an authorization code the server exchanges with its client secret; Facebook's
// JS SDK gives a user access token the server checks against the Graph API.
//
// Browsers only allow a popup straight from a click, so load the SDKs ahead of
// time (loadSocialSdks on page mount) and call requestSocialCredential
// synchronously inside the click handler.

import { SocialLoginPayload, SocialProvider } from '../types/api';

// Keep in sync with FACEBOOK_GRAPH_API_VERSION in the backend settings.py.
const FACEBOOK_GRAPH_API_VERSION = 'v23.0';

// Public IDs (not secrets): Azure serves them in /config.js, local dev reads
// them from src/frontend/.env. An empty value hides that provider's button.
export const socialConfig: Record<SocialProvider, string> = {
  google: window.APP_CONFIG?.googleClientId || process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
  facebook: window.APP_CONFIG?.facebookAppId || process.env.REACT_APP_FACEBOOK_APP_ID || '',
};

export const enabledSocialProviders = (Object.keys(socialConfig) as SocialProvider[]).filter(
  (p) => !!socialConfig[p]
);

/** The user closed or cancelled the provider popup: not an error to show. */
export class SocialLoginCancelled extends Error {}

/** The provider's popup or SDK failed; the message is meant for the user. */
export class SocialLoginError extends Error {}

// --- Minimal typings for the two SDKs (only what's used here) ---
interface GoogleCodeResponse {
  code?: string;
  error?: string;
  error_description?: string;
}
interface GoogleCodeClient {
  requestCode: () => void;
}
interface GoogleSdk {
  accounts: {
    oauth2: {
      initCodeClient: (config: {
        client_id: string;
        scope: string;
        ux_mode: 'popup';
        callback: (response: GoogleCodeResponse) => void;
        error_callback?: (error: { type: string }) => void;
      }) => GoogleCodeClient;
    };
  };
}
interface FacebookSdk {
  init: (params: { appId: string; version: string; cookie?: boolean; xfbml?: boolean }) => void;
  login: (
    callback: (response: { authResponse?: { accessToken: string } | null }) => void,
    options: { scope: string; auth_type?: string }
  ) => void;
}
declare global {
  interface Window {
    google?: GoogleSdk;
    FB?: FacebookSdk;
  }
}

const SDK_URLS: Record<SocialProvider, string> = {
  google: 'https://accounts.google.com/gsi/client',
  facebook: 'https://connect.facebook.net/en_US/sdk.js',
};

const sdkLoads: Partial<Record<SocialProvider, Promise<void>>> = {};

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Could not load ${src}`));
    document.head.appendChild(script);
  });
}

/** Load (once) the SDK for one provider. A failed load can be retried. */
export function loadSocialSdk(provider: SocialProvider): Promise<void> {
  if (!sdkLoads[provider]) {
    sdkLoads[provider] = loadScript(SDK_URLS[provider])
      .then(() => {
        if (provider === 'facebook') {
          window.FB!.init({
            appId: socialConfig.facebook,
            version: FACEBOOK_GRAPH_API_VERSION,
            cookie: false,
            xfbml: false,
          });
        }
      })
      .catch((err) => {
        delete sdkLoads[provider];
        throw err;
      });
  }
  return sdkLoads[provider]!;
}

function requestGoogleCode(): Promise<SocialLoginPayload> {
  return new Promise((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initCodeClient({
      client_id: socialConfig.google,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: (response) => {
        if (response.code) resolve({ code: response.code });
        else if (response.error === 'access_denied') reject(new SocialLoginCancelled());
        else reject(new SocialLoginError(response.error_description || 'Google sign-in failed.'));
      },
      error_callback: (error) => {
        if (error.type === 'popup_closed') reject(new SocialLoginCancelled());
        else reject(new SocialLoginError('Could not open the Google sign-in window. Allow pop-ups and try again.'));
      },
    });
    client.requestCode();
  });
}

function requestFacebookToken(): Promise<SocialLoginPayload> {
  return new Promise((resolve, reject) => {
    window.FB!.login(
      (response) => {
        if (response.authResponse?.accessToken) resolve({ access_token: response.authResponse.accessToken });
        else reject(new SocialLoginCancelled());
      },
      // 'rerequest' asks again for email if it was declined on an earlier attempt.
      { scope: 'email,public_profile', auth_type: 'rerequest' }
    );
  });
}

/**
 * Open the provider's popup and resolve with the credential to send to the
 * backend. Call from a click handler, after loadSocialSdk(provider) resolved.
 */
export function requestSocialCredential(provider: SocialProvider): Promise<SocialLoginPayload> {
  return provider === 'google' ? requestGoogleCode() : requestFacebookToken();
}
